import React from "react";
import { Hammer, ShieldCheck, Clock, Leaf } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Seo } from "../components/Seo";
import { CTABand } from "../components/Layout";
import { IMAGES, VALUES, TEAM } from "../data";

const ICONS = { Hammer, ShieldCheck, Clock, Leaf };

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="About Us | Bright Oak Renovations"
      description="Meet Bright Oak Renovations — a Cambridge-based general contractor with 15+ years building trust, one home at a time across Waterloo Region."
      path="/about"
    />
    <PageHeader
      label="Our Story"
      title="Building trust, one home at a time"
      subtitle="Bright Oak Renovations was founded on a simple belief — that a renovation should feel as good as it looks, and that homeowners deserve a contractor they can genuinely rely on."
      testid="about-header"
    />

    {/* STORY */}
    <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <img src={IMAGES.team} alt="Bright Oak craftsmanship" className="w-full h-[480px] object-cover" />
          <div className="absolute -bottom-6 -right-6 hidden md:block bg-[#C68B4E] text-white p-8 max-w-[220px]">
            <div className="text-4xl font-extrabold">15+</div>
            <div className="text-xs font-bold uppercase tracking-widest mt-2">Years of trusted craftsmanship</div>
          </div>
        </div>
        <div>
          <p className="section-label">Rooted in Cambridge</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 leading-tight">
            From a single truck to Waterloo Region's trusted renovators.
          </h2>
          <div className="space-y-5 text-[#5C5C5C] mt-6 leading-relaxed">
            <p>
              What began as one carpenter with a passion for honest work has grown into a full-service
              general contracting team — but our values haven't changed. We still answer the phone,
              still show up when we say we will, and still sweat the details others overlook.
            </p>
            <p>
              The name "Bright Oak" reflects how we build: like an oak, we grow slowly and deliberately,
              rooted in craft and made to stand the test of time. Every project is a chance to earn a
              neighbour's trust — which is why nearly all of our work comes from referrals.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* VALUES */}
    <section className="bg-white border-y border-[#2B2B2B]/10 py-20 lg:py-28" data-testid="values-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <p className="section-label">Why Choose Us</p>
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 mb-14">The values behind every build</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v) => {
            const Icon = ICONS[v.icon];
            return (
              <div key={v.title} data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`} className="bg-[#F7F3EC] border border-[#2B2B2B]/10 p-8 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#7A8B6F]/15 flex items-center justify-center">
                  <Icon size={26} strokeWidth={1.5} className="text-[#7A8B6F]" />
                </div>
                <h3 className="text-lg font-bold mt-5">{v.title}</h3>
                <p className="text-sm text-[#5C5C5C] mt-3 leading-relaxed">{v.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* TEAM */}
    <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28" data-testid="team-section">
      <p className="section-label">Meet the Team</p>
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 mb-14">The people behind Bright Oak</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {TEAM.map((m) => (
          <div key={m.name} data-testid={`team-${m.name.toLowerCase().replace(/\s+/g, "-")}`} className="group overflow-hidden bg-white border border-[#2B2B2B]/10">
            <div className="h-80 overflow-hidden">
              <img src={m.avatar} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-6">
              <h3 className="text-lg font-bold">{m.name}</h3>
              <p className="text-sm text-[#C68B4E] font-semibold mt-1">{m.role}</p>
            </div>
          </div>
        ))}
      </div>
    </section>

    <CTABand />
  </div>
);

export default About;
